<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { RouterView } from 'vue-router'
import {
  onMounted,
  defineAsyncComponent,
  ref,
  provide,
  onUnmounted,
  watch,
  nextTick,
  computed
} from 'vue'
import {
  deviceManagementService,
  deviceManagementPrintService,
  useDeviceManagementStore,
  getDeviceAccessToken,
  type DmPrintConfig
} from '@nsftx/seven-client-retail-vue-dm'
import {
  cmsService,
  useNotification,
  useSevenIgamingPlatformConfig,
  useTokenPayloadStore,
  useVoucherConfig,
  type NotificationEventConfig
} from '@nsftx/seven-client-retail-vue-sgp'
import { httpLogPlugin, isVoucher, logService, ScanService } from '@nsftx/seven-client-retail-utils'
import { getAppRunUuid } from '@nsftx/seven-client-retail-vue-common'
import { useSpinnerStore } from '@nsftx/seven-nxg-retail-vue-sdk'
import { NXGRetailLoadingOverlay, NXGRetailNotifications } from '@nsftx/seven-nxg-retail-vue-sdk/ui'
import '../../styles/main.scss'
import STHeader from './header/STHeader.vue'
import STFooter from './footer/STFooter.vue'
import STLobbyNavigation from './lobby/STLobbyNavigation.vue'
import { useLobbyStore } from '@/stores/lobby/lobbyStore'
import { useCmsConfigStore } from '@/stores/cms/cmsConfigStore'
import { useGameConfigurationStore } from '@/stores/cms/gameConfigurationStore'
import STIdleWarningModal from './STIdleWarningModal.vue'
import STServiceModeModal from './STServiceModeModal.vue'
import { HOME_MAIN } from '@/utils/injections/injectionKeys'
import STTicketCheck from './STTicketCheck.vue'
import STTicketCheckDetails from './ticket-check-details/STTicketCheckDetails.vue'
import ticketCheckService from '@/modules/ticket-check/ticketCheckService'
import { usePrinterStatus } from '@/composables/usePrinterStatus/usePrinterStatus'
import errorParser from '@/utils/errorParser'
import { initWorkingTimeService } from '@/modules/working-time/services/workingTimeService'
import { initAcceptors } from '@/modules/acceptors/acceptorsService'
import moneyProcessingService from '@/modules/money-processing/moneyProcessingService'
import { initWallets } from '@/modules/wallet/walletService'
import { initTicketPayout } from '@/modules/ticket-payout/ticketPayoutService'
import printService from '@/modules/print/services/printService'
import STVoucherDetails from './voucher/STVoucherDetails.vue'
import STVoucherPin from './voucher/STVoucherPin.vue'
import { handleScannedVoucher } from '@/modules/voucher/voucherSSBTService'
import { initSSBTIntegrator } from '@/modules/integrator/services/integratorSsbtService'
import { bootGames } from '@/modules/games/gameBooting'
import { initTicketPayin } from '@/modules/ticket-payin/ticketPayinService'
import { resolveLandingRoute } from '@/modules/landing-page/resolveLandingRoute'
import { applyTenantNumberFormat } from '@/modules/number-format/applyTenantNumberFormat'
import router from '@/router'
import {
  handleScreensaverOnScanEvent,
  initScreensaver
} from '@/modules/screensaver/screensaverService'
import STScreensaver from './screensaver/STScreensaver.vue'
import { useScreensaverStore } from '@/stores/screensaver/screensaverStore'

const LOG_PREFIX = '[SSBT]'
const scan = ScanService()

const isAppLoaded = ref<boolean>(false)
provide(HOME_MAIN, ref<HTMLElement>())

const { appData } = storeToRefs(useDeviceManagementStore())
const { isLobbyVisible, lobbyItemContent } = storeToRefs(useLobbyStore())
const { isSpinnerOpened } = storeToRefs(useSpinnerStore())
const { cmsConfig } = storeToRefs(useCmsConfigStore())
const notificationsPosition = computed(
  () => cmsConfig.value?.appSettings.notificationPosition ?? 'bottomMiddle'
)
const sevenIgamingPlatformConfig = useSevenIgamingPlatformConfig()
const voucherConfig = useVoucherConfig()
const accessToken = getDeviceAccessToken()
const tokenPayloadStore = useTokenPayloadStore()
const {
  initNotificationService,
  disconnectFromNotificationService,
  authenticateNotificationService
} = useNotification()
const { shouldShowScreensaver, screensaverData } = storeToRefs(useScreensaverStore())

watch(accessToken, (token) => {
  logService.info(`${LOG_PREFIX} Access token updated`, {
    code: 'SSBT_ACCESS_TOKEN_UPDATED',
    details: {
      hasToken: !!token
    }
  })
  if (token) {
    tokenPayloadStore.setToken(token)
    sevenIgamingPlatformConfig.token = token
  }
})

const waitForAccessToken = async () => {
  return new Promise<void>((resolve) => {
    let stopWatcher: ReturnType<typeof watch>
    // eslint-disable-next-line prefer-const
    stopWatcher = watch(
      accessToken,
      async (token) => {
        if (token) {
          resolve()
          await nextTick()
          stopWatcher()
        }
      },
      { immediate: true, flush: 'post' }
    )
  })
}

initTicketPayin()

onMounted(async () => {
  try {
    isSpinnerOpened.value = true

    const appRunUuid = getAppRunUuid()

    if (!appRunUuid) {
      logService.error(`${LOG_PREFIX} App run uuid is not defined`, {
        code: 'ST_APP_RUN_UUID_UNDEFINED'
      })
      return
    }

    await deviceManagementService.initDeviceManagement({ appName: 'Terminal NxG', appRunUuid })
    initWorkingTimeService()
    moneyProcessingService.initMoneyProcessingService()
    initTicketPayout()

    if (!appData.value) {
      logService.error(`${LOG_PREFIX} App data is not defined`, {
        appRunUuid,
        code: 'ST_DM_APP_DATA_UNDEFINED'
      })
      return
    }

    logService.info(`${LOG_PREFIX} App data received`, {
      appRunUuid,
      code: 'ST_DM_APP_DATA_RECEIVED'
    })
    httpLogPlugin.setAdditionalLogData({
      app_run_uuid: appRunUuid,
      tenant_id: appData.value.betshop.companyUuid,
      tenant_name: appData.value.betshop.companyName,
      device_id: appData.value.device.uuid,
      betshop_id: appData.value.betshop.uuid
    })
    await waitForAccessToken()

    const isProduction = import.meta.env.MODE === 'production'
    const config: NotificationEventConfig = {
      tenantId: appData.value?.betshop.companyUuid || '',
      deviceId: appData.value?.device.uuid || '',
      env: isProduction ? 'production' : 'staging',
      debug: !isProduction,
      authToken: accessToken?.value,
      onConnectionIssue: (issue) => {
        logService.warn(`${LOG_PREFIX} Connection issue has ocurred.`, {
          code: 'ST_NOTIFICATION_EVENT_CONNECTION_ISSUE',
          upstream_code: issue.type,
          upstream_message:
            issue.data instanceof CloseEvent
              ? `Websocket closed with code ${issue.data.code} and reason: ${issue.data.reason}`
              : 'Unknown connection issue',
          details: {
            wasClean: issue.data instanceof CloseEvent ? issue.data.wasClean : undefined
          }
        })
      }
    }
    initNotificationService(config)
    // the authenticateNotificationService must be callled after initNotificationService and after we have the access token,
    // otherwiese authenticateNotificationService will htorw an error because it depends on the notification service being initialized and on the access token being available
    // this code in this file needs to be refactored in the future to avoid this kind of dependencies between calls
    watch(accessToken, (token) => {
      if (token) {
        authenticateNotificationService(token)
      }
    })

    const cmsConfig = await cmsService.initCmsService('ssbt', {
      activeGames: appData.value.appConfig.data.appConfig.products
    })
    useCmsConfigStore().setCmsConfig(cmsConfig)
    applyTenantNumberFormat(cmsConfig.appSettings?.decimalFormat)
    useGameConfigurationStore().setGameConfigurations(cmsConfig.gameConfigurations.docs)
    bootGames()
    initSSBTIntegrator()
    initWallets()
    initScreensaver()
    await voucherConfig.initVoucherConfig(appData.value.betshop.companyUuid).catch((error) => {
      logService.error(`${LOG_PREFIX} Failed to get voucher config`, {
        ...errorParser.parseUpstream(error),
        code: 'ST_VOUCHER_CONFIG_INIT_ERROR'
      })
    })

    isAppLoaded.value = true
    isSpinnerOpened.value = false

    const printConfig: DmPrintConfig = {
      persist: { prefix: 'ssbt', storage: 'localStorage' },
      areAcceptorsDisabledOnPaperLow: cmsConfig.appSettings.areAcceptorsDisabledOnPaperLow
    }
    await deviceManagementPrintService
      .init(printConfig)
      .then(printService.initPrintService)
      .catch((error) => {
        logService.error(`${LOG_PREFIX} Failed to complete print service initialization`, {
          ...errorParser.parseUpstream(error),
          code: 'ST_PRINT_SERVICE_INIT_ERROR'
        })
      })

    initAcceptors()

    const landingRoute = resolveLandingRoute(cmsConfig)
    if (landingRoute.name !== 'home') {
      await router.replace(landingRoute)
    }

    addScanListener()
  } catch (error) {
    logService.error(`${LOG_PREFIX} Failed to initialize app in App.vue`, {
      ...errorParser.parseUpstream(error),
      code: 'ST_APP_INITIALIZATION_ERROR'
    })
  } finally {
    usePrinterStatus()
  }
})

onUnmounted(() => {
  scan.stopListeningForScannerEvents()
  disconnectFromNotificationService()
})

const addScanListener = () => {
  scan.listenForScannerEvents((res) => {
    handleScreensaverOnScanEvent()

    if (isVoucher(res.data.code)) {
      handleScannedVoucher(res.data.code)
    } else {
      ticketCheckService.handleScannedTicket(res.data.code)
    }
  })
}

const getAsyncComponents = () => {
  const STLobby = defineAsyncComponent(
    () =>
      import(
        /* webpackChunkName: "lobby" */
        '@/ui/components/lobby/STLobby.vue'
      )
  )

  return {
    STLobby
  }
}

const { STLobby } = getAsyncComponents()
</script>

<template>
  <div class="st-app">
    <STHeader>
      <template #navigation-menu>
        <STLobbyNavigation v-if="lobbyItemContent" />
      </template>
    </STHeader>
    <div class="st-router-wrapper">
      <RouterView v-if="isAppLoaded" :key="$route.fullPath" />
    </div>
    <NXGRetailLoadingOverlay :visible="isSpinnerOpened" />
    <STFooter />
    <STLobby v-if="isLobbyVisible" />
    <NXGRetailNotifications :position="notificationsPosition" />
    <STIdleWarningModal />
    <STServiceModeModal />
    <STTicketCheck />
    <STTicketCheckDetails />
    <STVoucherDetails />
    <STVoucherPin />
    <STScreensaver
      v-if="shouldShowScreensaver"
      :url="screensaverData.url!"
      :media-source="screensaverData.mediaSource!"
    />
  </div>
</template>

<style lang="scss">
.st-app {
  --st-app-bar-height: 72px;
  --st-content-height: calc(100vh - 2 * var(--st-app-bar-height));

  height: 100vh;
  display: grid;
  grid-template-rows: var(--st-app-bar-height) var(--st-content-height) var(--st-app-bar-height);
  background-color: var(--nxgds-mapped-color-surface-component-page);
}

.st-router-wrapper {
  width: 100%;
  height: 100%;
}
</style>
